import { useState, useEffect } from "react";
import { Box, Fab, IconButton, Paper } from "@mui/material";
import { ChatBox } from "./ChatBox";
import { useScreenSize } from "../../hooks/useScreenSize";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";

interface AltChatUIProps {
  view: string;
  showChatIcon?: boolean;
}

export const AltChatUI: React.FC<AltChatUIProps> = ({
  view,
  showChatIcon = false
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatEnabled, setIsChatEnabled] = useState(false);

  useEffect(() => {
    // Check localStorage for oliverChatEnabled key
    const chatEnabled = localStorage.getItem("oliverChatEnabled");
    setIsChatEnabled(chatEnabled === "true");
  }, []);

  // If chat is not enabled in localStorage, don't render anything
  if (!isChatEnabled) {
    return null;
  }

  const toggleChat = () => {
    setIsChatOpen((prev) => !prev);
  };

  // Only check showChatIcon prop to determine display mode
  if (showChatIcon) {
    return (
      <>
        {!isChatOpen && (
          <Fab
            color="primary"
            aria-label="chat"
            onClick={toggleChat}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1000
            }}
          >
            <ChatIcon />
          </Fab>
        )}

        {isChatOpen && (
          <Paper
            elevation={3}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              width: "calc(100% - 32px)",
              maxWidth: showChatIcon ? "350px" : "400px",
              height: showChatIcon ? "500px" : "600px",
              display: "flex",
              flexDirection: "column",
              zIndex: 1000,
              borderRadius: 2,
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 1,
                backgroundColor: "primary.main",
                color: "white"
              }}
            >
              <IconButton
                size="small"
                onClick={toggleChat}
                aria-label="Close chat"
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
              <ChatBox view={view} showChatIcon={showChatIcon} />
            </Box>
          </Paper>
        )}
      </>
    );
  }

  // When showChatIcon is false, render the original layouts
  if (view === "columnView") {
    return (
      <Box sx={{ mt: "45px", width: "15%" }}>
        <ChatBox view={view} />
      </Box>
    );
  }
  if (view === "weeklyView") {
    return (
      <Box sx={{ mt: "45px", width: "15%" }}>
        <ChatBox view={view} />
      </Box>
    );
  }
  if (view === "availability") {
    return (
      <Box sx={{ mt: "45px", width: "100%" }}>
        <ChatBox view={view} />
      </Box>
    );
  }
  if (view === "availability-review") {
    return (
      <Box sx={{ width: "20%" }} border={1} borderColor="grey.300">
        <ChatBox view={view} />
      </Box>
    );
  }
  if (view === "monthView") {
    return (
      <Box
        sx={{
          mt: 0.8,
          height: { xl: "calc(100vh - 400px)" }
        }}
      >
        <ChatBox view={view} />
      </Box>
    );
  }
  if (view === "meetingConfirmationView") {
    return (
      <Box
        border={1}
        borderColor="grey.300"
        sx={{
          mt: 0.8,
          width: "300px",
          height: "540px"
        }}
      >
        <ChatBox view={view} />
      </Box>
    );
  }
  return <></>;
};
